import { API_BASE_URL, API_HEADERS } from '../config';
import { Ship } from '../interfaces/types';

export const fetchData = async (endpoint: string) => {
  try {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const url = `${API_BASE_URL}/${cleanEndpoint}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: API_HEADERS,
      mode: 'cors'
    });
    
    if (!response.ok) {
      await response.text(); // Error text para debugging futuro
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();

    // Si la respuesta tiene estructura de paginación, devolver la estructura completa
    if (data.hasOwnProperty('data') && data.hasOwnProperty('pagination')) {
      return data;
    }
    
    // Si no tiene paginación, devolver directamente los datos
    return data;
    
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('Error de conexión - Verifica la URL y la conectividad');
    }
    throw error;
  }
};

interface AddShipToFleetParams {
  ship: Ship;
  username: string;
  isAnonymous: boolean;
  ingame?: boolean;
  insurance?: string;
}

export const addShipToFleet = async ({ ship, username, isAnonymous, ingame, insurance }: AddShipToFleetParams) => {
  const shipData = {
    // Campos básicos
    afterburner_speed: ship.afterburner_speed,
    beam: ship.beam,
    cargo_capacity: ship.cargo_capacity,
    height: ship.height,
    length: ship.length,
    mass: ship.mass,
    max_crew: ship.max_crew,
    min_crew: ship.min_crew,
    production_status: ship.production_status,
    scm_speed: ship.scm_speed,
    size: ship.size,
    type: ship.type,
    
    // Campos descriptivos
    description: ship.description,
    focus: ship.focus,
    name: ship.name,
    url: ship.url,
    imagen: ship.imagen,
    
    // Información del fabricante
    manufacturer: {
      id: ship.manufacturer.id,
      code: ship.manufacturer.code,
      description: ship.manufacturer.description,
      known_for: ship.manufacturer.known_for,
      name: ship.manufacturer.name,
      imagen: ship.manufacturer.imagen
    },
    
    // Sistemas compilados
    compiled: ship.compiled,
    
    // Usuario
    usuario: isAnonymous ? 'anonymous' : username,
    
    // Tipo de adquisición
    ingame: ingame !== undefined ? ingame : true,
    
    // Seguro (solo para naves compradas con dinero)
    ...(insurance && { insurance })
  };

  const response = await fetch(`${API_BASE_URL}/flota/nave`, {
    method: 'POST',
    headers: {
      ...API_HEADERS,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(shipData)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error adding ship to fleet: ${response.statusText} - ${errorText}`);
  }

  return response.json();
};

export const removeShipFromFleet = async (shipName: string, username: string) => {
  try {
    const url = `${API_BASE_URL}/flota/nave?name="${encodeURIComponent(shipName)}"&usuario=${encodeURIComponent(username)}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        ...API_HEADERS,
        'Content-Type': 'application/json'
      }
    });

    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${responseText}`);
    }

    return { success: true, message: `La nave ${shipName} ha sido eliminada correctamente` };
  } catch (error) {
    throw error;
  }
}; 